import React from "react";
import module_style from "./style.module.css";
import style from "../../style.module.css";
import {
  Input,
  Button,
  DatePicker,
  Tooltip,
} from "@my-scoot/component-library-legacy";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import AddCircle from "@material-ui/icons/AddCircle";
import ExlyModal from "common/Components/ExlyModal";
import moment from "moment";
import { is_empty, validURL } from "utils/validations";
import { api, dataProvider } from "data";
import constants from "constants/constants";
import { useNotifications } from "utils/hooks";
import { ImageUploader } from "ui/pages/TemplatePreview/widgets";
import MediaList from "./Components/MediaList";
import {
  date_format,
  getSelectedData,
  MEDIA_MENTION_COVER_CROP,
  MEDIA_MENTION_CROP,
  refreshIframe,
  validateData,
} from "./Utils";
import placeholderImg from "assets/images/image-uploader-placeholder.png";
import media_cover_placeholder from "assets/images/media_cover_placeholder.png";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import {
  HEADING_MAX_LENGTH,
  SIDEBAR_KEYS,
  SECTIONKEY_TO_MISC_KEY,
} from "ui/pages/TemplatePreview/constants.js";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import { IMAGE_SECTION_KEY_PATH } from "features/Common/modules/ExlyImage/constants/ExlyImage.constants";
import SectionVisibilityToggle from "../hideShow";
import { handleVisibilityToggle } from "../../utils";
import useVisibilityMutations from "../../useVisibilityMutations.hook";

const MediaMentions = ({ disableAllEdits }) => {
  const { notify } = useNotifications();
  const { miscellaneous_data, fetchMiscellaneousData, set_miscellaneous_data } =
    React.useContext(WebpageCreationContext);

  const [section_name, set_section_name] = React.useState("Media mentions");
  const [show_modal, set_show_modal] = React.useState(false);
  const [uuid, set_uuid] = React.useState(null);
  const [processing, set_processing] = React.useState(false);
  const [selected_id, set_selected_id] = React.useState(null);
  const [data, set_data] = React.useState([]);
  const [error_object, set_error_object] = React.useState({});
  const [sectionNameLimitHit, setSectionNameLimitHit] = React.useState(false);
  const [visible, set_visible] = React.useState(true);
  const draftDataRef = React.useRef(null);

  // Mutations for visibility toggle
  const { createMutation, updateMutation } = useVisibilityMutations();

  React.useEffect(() => {
    assignData();
  }, [miscellaneous_data]);

  const assignData = async () => {
    try {
      if (is_empty(miscellaneous_data?.media)) return;
      let temp = miscellaneous_data?.media[0];
      set_data(temp?.data?.data);

      const backendSectionName = temp?.data?.section_name;

      set_section_name(
        backendSectionName && backendSectionName.trim()
          ? backendSectionName
          : section_name
      );

      set_uuid(temp?.uuid);

      // SAFETY NET: default visible = true
      set_visible(temp?.data?.visible ?? true);
    } catch (err) {
      console.error("assign media err", err);
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        "error"
      );
    }
  };

  const handleSave = async ({
    currentData,
    selectedId,
    currentUuid,
    isVisible,
  }) => {
    try {
      if (processing) return;
      set_processing(true);

      const draft = draftDataRef?.current;

      const hasAllDraftData =
        draft?.media_logo &&
        draft?.cover_image &&
        draft?.organisation_name &&
        draft?.media_title &&
        draft?.date &&
        draft?.link;

      const hasActualData =
        Array.isArray(currentData) &&
        currentData.some(
          (item) =>
            !item.draft &&
            (item.media_logo ||
              item.cover_image ||
              item.organisation_name ||
              item.media_title ||
              item.date ||
              item.link)
        );

      if (!hasActualData && !hasAllDraftData) {
        set_show_modal(false);
        set_selected_id(null);
        return;
      }

      let selected_data = null;

      if (Number.isInteger(selectedId)) {
        selected_data =
          getSelectedData(currentData, selectedId) || draftDataRef.current;

        if (!selected_data) {
          notify("No data selected", "error");
          return;
        }

        const error_obj = validateData(selected_data);

        if (!is_empty(error_obj)) {
          set_error_object(error_obj);
          notify("Invalid form values", "error");
          return;
        }
      }

      let payload, api_resource;
      let temp = [...currentData];

      if (temp[selectedId]?.draft) {
        delete temp[selectedId].draft;
      }

      if (is_empty(currentUuid)) {
        api_resource = api.creator_miscellaneous_data.create;
        payload = {
          type: creator_miscellaneous_data_types.MEDIA_MENTIONS,
          data: { section_name, visible: isVisible, data: temp },
        };
      } else {
        api_resource = api.creator_miscellaneous_data.update;
        payload = {
          uuid: currentUuid,
          updated_values: {
            data: { section_name, visible: isVisible, data: temp },
          },
        };
      }

      const status = await dataProvider.custom_request(
        api_resource,
        "POST",
        payload
      );

      if (status.status === 200) {
        refreshIframe();
        set_data(temp);
        set_show_modal(false);
        set_selected_id(null);
      }

      notify(status?.message);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        "error"
      );
    } finally {
      set_processing(false);
    }
  };

  const handleDelete = () => {
    if (!Number.isInteger(selected_id)) return;

    const temp = [...data];
    temp.splice(selected_id, 1);

    const updatedData = temp.map((item, index) => ({
      ...item,
      id: index,
    }));

    set_data(updatedData);

    handleSave({
      currentData: updatedData,
      selectedId: null,
      currentUuid: uuid,
      isVisible: visible,
    });
  };

  const updateData = (value, key) => {
    let temp = data;
    temp[selected_id][key] = value;
    set_data([...temp]);
  };

  const handleModalClose = () => {
    set_show_modal(false);
    set_error_object({});
    set_selected_id(null);

    // remove draft entries
    let published_data = data.filter((item) => !item.draft);
    set_data([...published_data]);
  };

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.MEDIA_MENTIONS],
      sectionType: creator_miscellaneous_data_types.MEDIA_MENTIONS,
      currentSectionName: section_name,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation: createMutation,
      updateMutation: updateMutation,
      currentData: data,
    });

  return (
    <div>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      <div className={style.label}>
        What would you like to call this section?
      </div>

      <div className={`mt-2`}>
        <Input
          fullWidth
          value={section_name}
          inputProps={{
            maxLength: HEADING_MAX_LENGTH,
          }}
          error={sectionNameLimitHit}
          helperText={
            sectionNameLimitHit
              ? `Maximum field length of ${HEADING_MAX_LENGTH} reached.`
              : null
          }
          onChange={(e) => {
            const value = e.target.value;

            set_section_name(value);
            setSectionNameLimitHit(value.length >= HEADING_MAX_LENGTH);
          }}
          disabled={disableAllEdits}
        />
      </div>

      <div className={`${style.info} tw-my-[16px]`}>
        <InfoOutlined className={`${style.infoIcon}`} />
        The items can be dragged up and down using the &#10303; icon to change
        the order in which they appear.
      </div>

      {!is_empty(data) && (
        <MediaList
          data={data}
          set_show_modal={(value) => set_show_modal(value)}
          set_data={(value) => set_data(value)}
          uuid={uuid}
          handleDelete={() => handleDelete()}
          selected_id={selected_id}
          set_selected_id={(value) => set_selected_id(value)}
          disableAllEdits={disableAllEdits}
        />
      )}

      <div className={`d-flex mt-4 align-items-center justify-content-between`}>
        {disableAllEdits ? (
          <div />
        ) : (
          <div
            onClick={() => {
              if (processing) return;
              let initial_data = {
                id: (data || []).length,
                media_logo: "",
                cover_image: "",
                organisation_name: "",
                media_title: "",
                date: null,
                link: "",
                draft: true,
              };
              set_selected_id(data.length);
              set_data([...data, initial_data]);
              draftDataRef.current = initial_data;
              set_show_modal(true);
            }}
            className={`${module_style.action_color} ${module_style.text_cta} pointer`}
          >
            <AddCircle />
            <span className={`ml-2`}>Add media mention</span>
          </div>
        )}

        <Button
          variant="contained"
          color="primary"
          disableElevation
          disabled={processing || disableAllEdits}
          loading={processing}
          onClick={() =>
            handleSave({
              currentData: data,
              selectedId: selected_id,
              currentUuid: uuid,
              isVisible: visible,
            })
          }
        >
          Save
        </Button>
      </div>

      <ExlyModal
        title="Add a media mention"
        open={show_modal}
        onClose={handleModalClose}
        primaryBtnText={processing ? "..." : "Save"}
        onPrimaryBtnClick={() =>
          handleSave({
            currentData: data,
            selectedId: selected_id,
            currentUuid: uuid,
            isVisible: visible,
          })
        }
        modal_props={{
          modalPaperClassName: module_style.miscellaneous_data_modal,
        }}
      >
        {Number.isInteger(selected_id) && data[selected_id] && (
          <div className={`pt-4 pb-4`}>
            <div className={`${style.label} ${style.required}`}>Media logo</div>
            <div className={`d-flex mt-2`}>
              <div>
                <ImageUploader
                  fullWidth={600}
                  customDefaultImage={placeholderImg}
                  hideRecommendedText
                  uploadUrl={data[selected_id]?.media_logo}
                  setUploadUrl={(firebaseUrl) => {
                    if (error_object?.media_logo) {
                      set_error_object({ ...error_object, media_logo: false });
                    }
                    updateData(firebaseUrl, "media_logo");
                  }}
                  hoverText="Upload Image"
                  width={MEDIA_MENTION_CROP.width}
                  height={MEDIA_MENTION_CROP.height}
                  borderRadius="4px"
                  query={"media"}
                  id="website_media_logo"
                  uploadViaOptimization
                  sectionName={IMAGE_SECTION_KEY_PATH.HOME_PAGE}
                />
                {error_object?.media_logo && (
                  <div className={`text-danger small`}>Invalid</div>
                )}
              </div>

              <div className={`ml-4 ${style.info}`}>
                Recommended format:
                <br />
                {MEDIA_MENTION_CROP.width} x {MEDIA_MENTION_CROP.height} pixels
                (PNG or JPG)
              </div>
            </div>

            <div className={`${style.label} ${style.required} mt-3 mb-1`}>
              Name of the organization
            </div>
            <Input
              error={error_object?.organisation_name}
              helperText={error_object?.organisation_name ? "Invalid" : null}
              fullWidth
              value={data[selected_id]?.organisation_name}
              onChange={(e) => {
                if (error_object?.organisation_name) {
                  set_error_object({
                    ...error_object,
                    organisation_name: false,
                  });
                }
                let value = e.target.value;
                updateData(value, "organisation_name");
              }}
            />

            <div className={`${style.label} ${style.required} mt-3 mb-1`}>
              Title of the media post/article
            </div>
            <Input
              error={error_object?.media_title}
              helperText={error_object?.media_title ? "Invalid" : null}
              fullWidth
              value={data[selected_id]?.media_title}
              onChange={(e) => {
                if (error_object?.media_title) {
                  set_error_object({ ...error_object, media_title: false });
                }
                let value = e.target.value;
                updateData(value, "media_title");
              }}
            />

            <div className={`${style.label} ${style.required} mt-3 mb-1`}>
              Link to the post/article
            </div>
            <Input
              error={error_object?.link}
              helperText={error_object?.link ? "Invalid" : null}
              fullWidth
              value={data[selected_id]?.link}
              onChange={(e) => {
                if (error_object?.link) {
                  set_error_object({ ...error_object, link: false });
                }
                let value = e.target.value;
                updateData(value, "link");
                if (!validURL(value)) {
                  set_error_object({ ...error_object, link: true });
                }
              }}
            />

            <div className={`${style.label} ${style.required} mt-3 mb-1`}>
              Date on which post/article was posted
            </div>
            <DatePicker
              maxDate={new Date()} //maxDate
              error={error_object?.date}
              helperText={error_object?.date ? "Invalid" : null}
              value={
                data[selected_id]?.date
                  ? moment(data[selected_id]?.date, date_format).toDate()
                  : null
              }
              onChange={(value) => {
                if (error_object?.date) {
                  set_error_object({ ...error_object, date: false });
                }
                updateData(moment(value).format(date_format), "date");
              }}
            />

            <div className={`${style.label} ${style.required} mt-4`}>
              Upload cover image
              <Tooltip
                title="On the webpage, this image will appear as the banner for your media article."
                color="primary"
                arrow
              >
                <HelpOutlineIcon className={`${module_style.help_svg}`} />
              </Tooltip>
            </div>

            <div className={`mb-2 ${style.info}`}>
              Recommended format: 400 x 300 pixels (PNG or JPG)
            </div>
            <div className={`d-flex`}>
              <ImageUploader
                photoWidgetClassName={`border-0`}
                fullWidth={600}
                customDefaultImage={media_cover_placeholder}
                hideRecommendedText
                uploadUrl={data[selected_id]?.cover_image}
                setUploadUrl={(firebaseUrl) => {
                  if (error_object?.cover_image) {
                    set_error_object({ ...error_object, cover_image: false });
                  }
                  updateData(firebaseUrl, "cover_image");
                }}
                hoverText="Upload Image"
                width={MEDIA_MENTION_COVER_CROP.width}
                height={MEDIA_MENTION_COVER_CROP.height}
                borderRadius="4px"
                query={"cover"}
                id="website_media_cover"
                uploadViaOptimization
                sectionName={IMAGE_SECTION_KEY_PATH.HOME_PAGE}
              />
            </div>
            {error_object?.cover_image && (
              <div className={`text-danger small`}>Invalid</div>
            )}
          </div>
        )}
      </ExlyModal>
    </div>
  );
};

export default MediaMentions;
